
import {
  calcLineAmount,
  formatAmount,
  formatQuantity,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import {
  instanceOfIVatTuResponseBaseWithApprovedStatus,
  instanceOfIVatTuWithSoLuongDaXuat,
} from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import {
  IVatTuResponseBase,
  IVatTuResponseBaseWithApprovedStatus,
  IVatTuWithSoLuongDaXuat,
} from "@/core/interfaces/ApiResponses";
import { defineComponent, PropType } from "vue";

import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import QuantityInputCustom from "@/components/forms-elements/QuantityInputCustom.vue";
import {
  VAT_TU_TABLE_COLUMN_WIDTHS,
  VatTuTable,
} from "@/components/shared/vat-tu-table";

export default defineComponent({
  name: "add-vat-tu-table-items",

  components: { ButtonCustom, QuantityInputCustom, VatTuTable },

  emits: ["on-delete", "so-luong-change", "update-ghi-chu"],

  setup() {
    const { ButtonTypeColors, ButtonsType } = useButtonCustom();

    const { push } = useRouterCustom();

    return {
      ButtonTypeColors,
      ButtonsType,
      columnWidths: VAT_TU_TABLE_COLUMN_WIDTHS,
      push,
    };
  },

  props: {
    disableGhiChuInput: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array as PropType<
        Array<
          | IVatTuResponseBase
          | IVatTuResponseBaseWithApprovedStatus
          | IVatTuWithSoLuongDaXuat
        >
      >,
      default: () => [],
    },
    isDeleteButtonDisplay: {
      type: Boolean,
      default: true,
    },
    isHighlightUnapprovedItem: {
      type: Boolean,
      default: false,
    },
    isInfoButtonDisplay: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: true,
    },
    isSoLuongEditable: {
      type: Boolean,
      default: false,
    },
    showGhiChuColumn: {
      type: Boolean,
      default: false,
    },
    showGhiChuInput: {
      type: Boolean,
      default: false,
    },
    showPriceAndTotal: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    items: {
      handler() {
        this.vatTuItems = this.items;
      },
      deep: true,
    },
  },

  data() {
    return {
      editingSoLuongById: {} as Record<number, number>,
      vatTuItems: this.items,
    };
  },

  methods: {
    isDeleteButtonDisplaying(index: number) {
      return (
        (instanceOfIVatTuWithSoLuongDaXuat(this.items[index]) &&
          (this.items[index] as IVatTuWithSoLuongDaXuat)
            .so_luong_da_xuat_kho === 0) ||
        !instanceOfIVatTuWithSoLuongDaXuat(this.items[index])
      );
    },

    isHighlight(index: number) {
      if (
        this.isHighlightUnapprovedItem &&
        instanceOfIVatTuResponseBaseWithApprovedStatus(this.items[index]) &&
        !(this.items[index] as IVatTuResponseBaseWithApprovedStatus).is_approved
      ) {
        return "m-0 flex align-items-center text-danger";
      }

      return "m-0 flex align-items-center";
    },

    formatQuantity,
    formatUnitPrice,
    formatAmount,
    calcLineAmount,

    onDeleteVatTu(id: number) {
      this.$emit("on-delete", id);
    },

    onDetailButtonClicked(id) {
      this.push(`/quan-ly/vat-tu/chi-tiet/${id}`);
    },

    onSoLuongFocus(vatTu: IVatTuWithSoLuongDaXuat) {
      this.editingSoLuongById[vatTu.id] = vatTu.so_luong;
    },

    onSoLuongChanged(vatTu: IVatTuWithSoLuongDaXuat, value: number) {
      const originalSoLuong =
        this.editingSoLuongById[vatTu.id] ?? vatTu.so_luong;

      if (
        instanceOfIVatTuWithSoLuongDaXuat(vatTu) &&
        vatTu.so_luong_da_xuat_kho > 0 &&
        value < originalSoLuong
      ) {
        vatTu.so_luong = originalSoLuong;
        return;
      }

      if (value == null || value < 0 || Number.isNaN(value)) {
        vatTu.so_luong = originalSoLuong;
        return;
      }

      this.editingSoLuongById[vatTu.id] = value;

      this.$emit("so-luong-change", {
        selectingVatTu: vatTu,
        so_luong_moi: value,
      });
    },

    soLuongDisplay(index: number) {
      if (!this.items[index]) return 0;

      if (instanceOfIVatTuWithSoLuongDaXuat(this.items[index])) {
        return formatQuantity(this.items[index].so_luong);
      }

      return formatQuantity(this.items[index].so_luong);
    },

    updateGhiChu(value: string, id: number) {
      this.$emit("update-ghi-chu", {
        ghi_chu: value,
        id,
      });
    },
  },
});
